import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, ChevronRight, ChevronLeft, Eye, EyeOff, Check,
  User, Mail, Lock, Phone, Calendar, MapPin, FileText,
  Camera, Upload, AlertCircle, CheckCircle2, CreditCard
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { ROUTE_PATHS, COUNTRIES, PLANS } from '@/lib/index';
import { useI18n } from '@/hooks/useI18n';
import { toast } from '@/hooks/use-toast';

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  address: string;
  city: string;
  country: string;
  postcode: string;
  id_document_type: 'passport' | 'driving_license' | 'national_id';
  plan: 'Standard' | 'Plus' | 'Premium' | 'Metal';
  password: string;
  confirm_password: string;
}

export default function Register() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const STEPS = [
    { id: 1, title: t('register_step1'), icon: User, description: t('register_step1_desc') },
    { id: 2, title: t('register_step2'), icon: MapPin, description: t('register_step2_desc') },
    { id: 3, title: t('register_step3'), icon: FileText, description: t('register_step3_desc') },
  ];

  const ID_TYPES = [
    { value: 'passport', label: t('id_type_passport') },
    { value: 'driving_license', label: t('id_type_driving_license') },
    { value: 'national_id', label: t('id_type_national_id') },
  ];

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [idFront, setIdFront] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [idFrontPreview, setIdFrontPreview] = useState('');
  const [selfiePreview, setSelfiePreview] = useState('');
  const [completed, setCompleted] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const idFrontRef = useRef<HTMLInputElement>(null);
  const selfieRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    city: '',
    country: 'GB',
    postcode: '',
    id_document_type: 'passport',
    plan: 'Standard',
    password: '',
    confirm_password: '',
  });

  const set = (field: keyof FormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleFile = (
    file: File | null,
    setter: (f: File | null) => void,
    previewSetter: (s: string) => void
  ) => {
    if (!file) return;
    setter(file);
    const reader = new FileReader();
    reader.onload = (e) => previewSetter(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file: File, userId: string, name: string): Promise<string> => {
    const ext = file.name.split('.').pop();
    const path = `${userId}/${name}.${ext}`;
    const { error } = await supabase.storage.from('kyc-documents').upload(path, file, { upsert: true });
    if (error) throw error;
    return path;
  };

  const validateStep = (): boolean => {
    setError('');
    if (step === 1) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.date_of_birth) {
        setError(t('error_required_fields'));
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setError(t('error_invalid_email'));
        return false;
      }
      const age = Math.floor((Date.now() - new Date(form.date_of_birth).getTime()) / (1000 * 60 * 60 * 24 * 365));
      if (age < 18) {
        setError(t('error_age_requirement'));
        return false;
      }
    }
    if (step === 2) {
      if (!form.address || !form.city || !form.postcode || !form.country) {
        setError(t('error_address_required'));
        return false;
      }
      if (!form.password || form.password.length < 8) {
        setError(t('error_password_length'));
        return false;
      }
      if (form.password !== form.confirm_password) {
        setError(t('error_password_mismatch'));
        return false;
      }
    }
    if (step === 3) {
      if (!idFront) {
        setError(t('error_id_required'));
        return false;
      }
      if (!selfie) {
        setError(t('error_selfie_required'));
        return false;
      }
      if (!termsAccepted) {
        setError(t('error_terms_required'));
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError('');

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            first_name: form.first_name,
            last_name: form.last_name,
            phone: form.phone,
            date_of_birth: form.date_of_birth,
            address: form.address,
            city: form.city,
            country: form.country,
            postcode: form.postcode,
            id_document_type: form.id_document_type,
            plan: form.plan,
          },
        },
      });

      if (authError) {
        if (authError.message.includes('already registered') || authError.message.includes('already exists') || authError.message.includes('User already registered')) {
          throw new Error(t('error_email_exists'));
        }
        if (authError.message.includes('security purposes') || authError.message.includes('rate limit') || authError.message.includes('too many') || authError.message.includes('For security')) {
          throw new Error(t('error_rate_limit'));
        }
        throw new Error(authError.message);
      }

      if (!authData.user) throw new Error(t('error_registration_failed'));

      const userId = authData.user.id;
      const hasSession = !!authData.session;

      let profileExists = false;
      for (let attempt = 0; attempt < 5; attempt++) {
        await new Promise((r) => setTimeout(r, 1000));
        const { data: checkProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', userId)
          .maybeSingle();
        if (checkProfile) {
          profileExists = true;
          break;
        }
      }

      if (!profileExists) {
        await supabase.from('profiles').insert({
          id: userId,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          date_of_birth: form.date_of_birth || null,
          address: form.address,
          city: form.city,
          country: form.country,
          postcode: form.postcode,
          id_document_type: form.id_document_type,
          kyc_status: 'submitted',
          plan: form.plan,
          is_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }).select();
        await new Promise((r) => setTimeout(r, 1000));
      }

      if (hasSession) {
        let idFrontUrl = '';
        let selfieUrl = '';
        try {
          if (idFront) idFrontUrl = await uploadFile(idFront, userId, 'id_front');
          if (selfie) selfieUrl = await uploadFile(selfie, userId, 'selfie');
        } catch (uploadErr) {
          console.warn('Document upload skipped:', uploadErr);
        }

        await supabase.from('profiles').update({
          id_document_front_url: idFrontUrl || null,
          selfie_url: selfieUrl || null,
          kyc_status: 'submitted',
          updated_at: new Date().toISOString(),
        }).eq('id', userId);

        const { data: existingAccount } = await supabase
          .from('bank_accounts')
          .select('id, account_number')
          .eq('user_id', userId)
          .maybeSingle();

        if (existingAccount) {
          setAccountNumber(existingAccount.account_number);
        } else {
          const accNum = Math.floor(10000000 + Math.random() * 90000000).toString();
          const iban = 'GB' + Math.floor(10 + Math.random() * 90).toString() + 'CYF204007' + accNum;
          await supabase.from('bank_accounts').insert({
            user_id: userId,
            account_number: accNum,
            sort_code: '04-00-75',
            iban: iban,
            bic: 'CYFBGB21',
            balance: 0,
            available_balance: 0,
            currency: 'GBP',
            status: 'active',
          });
          setAccountNumber(accNum);
        }
      }

      setCompleted(true);
      toast({
        title: t('success_generic'),
        description: t('register_success_message'),
        variant: 'default',
      });
      setTimeout(() => navigate(hasSession ? ROUTE_PATHS.DASHBOARD : ROUTE_PATHS.LOGIN), 3000);

    } catch (err) {
      const msg = err instanceof Error ? err.message : t('error_generic');
      setError(msg);
      toast({
        title: t('error_generic'),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (pwd: string): { label: string; color: string; bars: number } => {
    if (pwd.length === 0) return { label: '', color: 'bg-border', bars: 0 };
    if (pwd.length < 8) return { label: t('password_too_short'), color: 'bg-destructive', bars: 1 };
    if (pwd.length < 10) return { label: t('password_weak'), color: 'bg-orange-500', bars: 2 };
    if (pwd.length < 12 || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd)) return { label: t('password_moderate'), color: 'bg-yellow-500', bars: 3 };
    return { label: t('password_strong'), color: 'bg-success', bars: 4 };
  };

  const pwdStrength = getPasswordStrength(form.password);

  if (completed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-24 h-24 bg-success-light rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-success" />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-3">{t('register_success_title')}</h2>
          <p className="text-muted-foreground mb-2">{t('register_success_subtitle')}</p>
          {accountNumber && (
            <div className="mt-4 p-4 bg-card rounded-xl border border-border">
              <p className="text-sm text-muted-foreground mb-1">{t('dash_account_number')}</p>
              <p className="text-2xl font-bold text-accent font-mono">{accountNumber}</p>
            </div>
          )}
          <div className="mt-6 w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-muted-foreground mt-3">{t('redirecting')}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border/50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-accent" />
            <span className="text-lg font-bold text-foreground">CYF24 Bank</span>
          </Link>
          <span className="text-sm text-muted-foreground">Step {step} of {STEPS.length}</span>
        </div>
        <div className="h-1 bg-border/50">
          <motion.div
            className="h-full bg-accent"
            animate={{ width: `${(step / STEPS.length) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="pt-20 pb-12 min-h-screen flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-80 xl:w-96 bg-card/30 border-r border-border/50 flex-col p-8 pt-12">
          <h2 className="text-xl font-bold text-foreground mb-8">{t('register_title')}</h2>
          <div className="space-y-4">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === step;
              const isDone = s.id < step;
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                    isActive ? 'bg-accent/10 border border-accent/30' : isDone ? 'opacity-70' : 'opacity-40'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isDone ? 'bg-success-light text-success'
                    : isActive ? 'bg-accent/20 text-accent'
                    : 'bg-muted text-muted-foreground'
                  }`}>
                    {isDone ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className={`font-medium text-sm ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-auto pt-8 space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="w-4 h-4 text-accent" /> {t('fca_regulated')}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="w-4 h-4 text-accent" /> {t('fscs_protected')}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Lock className="w-4 h-4 text-accent" /> {t('ssl_encryption')}
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-start justify-center px-4 py-8 lg:py-12">
          <div className="w-full max-w-xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <div className="flex items-center gap-2 text-accent text-sm font-medium mb-2">
                    <span>Step {step} of {STEPS.length}</span>
                    <span>·</span>
                    <span>{STEPS[step - 1].description}</span>
                  </div>
                  <h1 className="text-3xl font-bold text-foreground">{STEPS[step - 1].title}</h1>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-xl p-4"
                  >
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
                    <p className="text-sm text-destructive">{error}</p>
                  </motion.div>
                )}

                {step === 1 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('register_first_name')} <span className="text-destructive">*</span></Label>
                        <Input placeholder={t('register_first_name_placeholder')} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('register_last_name')} <span className="text-destructive">*</span></Label>
                        <Input placeholder={t('register_last_name_placeholder')} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="h-12" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('register_email')} <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type="email" placeholder={t('register_email_placeholder')} value={form.email} onChange={(e) => set('email', e.target.value)} className="pl-10 h-12" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('register_phone')} <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type="tel" placeholder={t('register_phone_placeholder')} value={form.phone} onChange={(e) => set('phone', e.target.value)} className="pl-10 h-12" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('register_dob')} <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} className="pl-10 h-12" max={new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]} />
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t('register_address')} <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder={t('register_address_placeholder')} value={form.address} onChange={(e) => set('address', e.target.value)} className="pl-10 h-12" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('register_city')} <span className="text-destructive">*</span></Label>
                        <Input placeholder={t('register_city_placeholder')} value={form.city} onChange={(e) => set('city', e.target.value)} className="h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('register_postcode')} <span className="text-destructive">*</span></Label>
                        <Input placeholder={t('register_postcode_placeholder')} value={form.postcode} onChange={(e) => set('postcode', e.target.value)} className="h-12" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('register_country')} <span className="text-destructive">*</span></Label>
                      <select
                        value={form.country}
                        onChange={(e) => set('country', e.target.value)}
                        className="w-full h-12 px-3 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('register_id_type')} <span className="text-destructive">*</span></Label>
                      <div className="grid grid-cols-3 gap-3">
                        {ID_TYPES.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => set('id_document_type', opt.value as 'passport' | 'driving_license' | 'national_id')}
                            className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                              form.id_document_type === opt.value
                                ? 'border-accent bg-accent/10 text-accent'
                                : 'border-border bg-card text-muted-foreground hover:border-accent/50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('register_plan')} <span className="text-destructive">*</span></Label>
                      <div className="grid grid-cols-2 gap-3">
                        {PLANS.map((opt) => (
                          <button
                            key={opt.name}
                            type="button"
                            onClick={() => set('plan', opt.name as 'Standard' | 'Plus' | 'Premium' | 'Metal')}
                            className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                              form.plan === opt.name
                                ? 'border-accent bg-accent/10'
                                : `${opt.color} bg-card/50 hover:border-accent/50`
                            }`}
                          >
                            {opt.highlighted && (
                              <span className="absolute -top-2 left-3 bg-accent text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-full">★ {opt.badge}</span>
                            )}
                            <div className="flex items-center gap-2 mb-1">
                              <CreditCard className="w-4 h-4 text-accent" />
                              <span className="font-bold text-foreground">{opt.name}</span>
                            </div>
                            <span className="text-sm text-muted-foreground">£{opt.price}/{opt.period}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('register_password')} <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('register_password_placeholder')}
                          value={form.password}
                          onChange={(e) => set('password', e.target.value)}
                          className="pl-10 pr-10 h-12"
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {form.password && (
                        <>
                          <div className="grid grid-cols-4 gap-1 mt-2">
                            {[1, 2, 3, 4].map((bar) => (
                              <div key={bar} className={`h-1 rounded-full transition-colors ${bar <= pwdStrength.bars ? pwdStrength.color : 'bg-border'}`} />
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground">{pwdStrength.label}</p>
                        </>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>{t('register_confirm_password')} <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={showConfirm ? 'text' : 'password'}
                          placeholder={t('register_confirm_password_placeholder')}
                          value={form.confirm_password}
                          onChange={(e) => set('confirm_password', e.target.value)}
                          className="pl-10 pr-10 h-12"
                        />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {form.confirm_password && form.password !== form.confirm_password && (
                        <p className="text-xs text-destructive">{t('error_password_mismatch')}</p>
                      )}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5">
                    <p className="text-muted-foreground">
                      {t('register_upload_instructions')}
                    </p>
                    <div className="space-y-2">
                      <Label>{t('register_id_document')} <span className="text-destructive">*</span></Label>
                      <button
                        type="button"
                        onClick={() => idFrontRef.current?.click()}
                        className="w-full h-36 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:border-accent/50 transition-colors overflow-hidden relative"
                      >
                        {idFrontPreview ? (
                          <img src={idFrontPreview} alt="Document" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground text-center px-2">{t('register_upload_id')}</span>
                          </>
                        )}
                      </button>
                      <input ref={idFrontRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0] ?? null, setIdFront, setIdFrontPreview)} />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('register_selfie')} <span className="text-destructive">*</span></Label>
                      <button
                        type="button"
                        onClick={() => selfieRef.current?.click()}
                        className="w-full h-48 border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center gap-3 hover:border-accent/50 transition-colors overflow-hidden relative"
                      >
                        {selfiePreview ? (
                          <img src={selfiePreview} alt="Selfie" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <>
                            <Camera className="w-12 h-12 text-muted-foreground" />
                            <p className="text-muted-foreground font-medium">{t('register_take_selfie')}</p>
                            <p className="text-xs text-muted-foreground">{t('register_selfie_format')}</p>
                          </>
                        )}
                      </button>
                      <input ref={selfieRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => handleFile(e.target.files?.[0] ?? null, setSelfie, setSelfiePreview)} />
                    </div>

                    <div className="bg-muted/50 rounded-xl p-5 space-y-3">
                      <p className="text-sm font-medium text-foreground">{t('register_selfie_tips_title')}</p>
                      <ul className="space-y-2">
                        {[
                          t('register_selfie_tip1'),
                          t('register_selfie_tip2'),
                          t('register_selfie_tip3'),
                          t('register_selfie_tip4'),
                        ].map((tip) => (
                          <li key={tip} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check className="w-4 h-4 text-success shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-border text-accent focus:ring-accent"
                      />
                      <label htmlFor="terms" className="text-sm text-muted-foreground">
                        {t('register_terms_text')}{' '}
                        <Link to={ROUTE_PATHS.LEGAL} className="text-accent hover:underline">
                          {t('register_terms_link')}
                        </Link>
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  {step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep((s) => s - 1)}
                      className="flex-1 h-12"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      {t('register_back')}
                    </Button>
                  )}
                  {step < STEPS.length ? (
                    <Button
                      type="button"
                      onClick={nextStep}
                      className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
                    >
                      {t('register_next')}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                          {t('register_creating')}
                        </span>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          {t('register_submit')}
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {step === 1 && (
                  <p className="text-center text-sm text-muted-foreground">
                    {t('register_has_account')}{' '}
                    <Link to={ROUTE_PATHS.LOGIN} className="text-accent hover:underline font-medium">
                      {t('nav_login')}
                    </Link>
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}